import React, { useEffect, useMemo, useState } from 'react';
import friendlyError from '../../../utils/friendlyError';
import styled from 'styled-components';
import {
  FaSearch,
  FaCopy,
  FaTrash,
  FaThumbtack,
  FaSyncAlt,
  FaFileAlt,
  FaShareAlt,
  FaHashtag,
  FaCheck,
} from 'react-icons/fa';

import {
  TechSection,
  TechInput,
  TechSelect,
  TechButtonGhost,
  TechButtonDanger,
  StatusPill,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../../styles/techComponents';

import {
  listMaterials,
  deleteMaterial,
  updateMaterial,
} from '../../../services/messagingService';

import { TYPE_OPTIONS } from '../templates';

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 1rem;
  align-items: center;
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;

  svg {
    position: absolute;
    left: 0.7rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-disabled);
    font-size: 0.85rem;
  }
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 0.85rem;
`;

const Card = styled.article`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.012), transparent 35%),
    var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-left: 3px solid ${({ $pinned }) => ($pinned ? 'var(--alert-warning)' : accent)};
  border-radius: 6px;
  padding: 0.95rem 1.05rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px; height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }
`;

const CardTitle = styled.h4`
  margin: 0;
  color: var(--text-primary);
  font-size: 0.95rem;
  line-height: 1.35;
  word-break: break-word;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  font-family: ${mono};
  font-size: 0.6rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--text-disabled);

  span {
    background: ${panelDeep};
    border: 1px solid ${borderSoft};
    border-radius: 3px;
    padding: 1px 6px;
  }
  .type { color: ${accent}; border-color: rgba(0, 180, 216, 0.3); }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  font-family: ${mono};
  font-size: 0.62rem;
  color: #cfe9f4;
  background: rgba(0, 180, 216, 0.06);
  border: 1px solid rgba(0, 180, 216, 0.2);
  padding: 1px 5px;
  border-radius: 3px;
  letter-spacing: 0.04em;
`;

const Preview = styled.pre`
  margin: 0;
  padding: 0.7rem 0.85rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 4px;
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 0.84rem;
  line-height: 1.5;
  color: var(--text-secondary);
  max-height: ${({ $expanded }) => ($expanded ? 'none' : '180px')};
  overflow: hidden;
  position: relative;
  word-break: break-word;

  ${({ $expanded }) =>
    !$expanded &&
    `
    &::after {
      content: '';
      position: absolute;
      left: 0; right: 0; bottom: 0;
      height: 40px;
      background: linear-gradient(to bottom, transparent, rgba(11, 19, 32, 0.85));
      pointer-events: none;
    }
  `}
`;

const Actions = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function typeLabel(type) {
  return TYPE_OPTIONS.find((o) => o.value === type)?.label || type;
}

const MaterialsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    const params = {};
    if (typeFilter !== 'all') params.type = typeFilter;
    if (search.trim()) params.search = search.trim();

    listMaterials(params)
      .then((list) => setItems(list))
      .catch((err) => setError(friendlyError(err)))
      .finally(() => setLoading(false));
  };

  // recargar al cambiar filtros (con un pequeño debounce para search)
  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, typeFilter]);

  const totalsByType = useMemo(() => {
    const t = {};
    for (const m of items) t[m.type] = (t[m.type] || 0) + 1;
    return t;
  }, [items]);

  const remove = async (m) => {
    if (!window.confirm(`¿Eliminar "${m.title}"?`)) return;
    try {
      await deleteMaterial(m.id);
      load();
    } catch (err) {
      alert('No se pudo eliminar: ' + (friendlyError(err)));
    }
  };

  const togglePin = async (m) => {
    try {
      await updateMaterial(m.id, { pinned: !m.pinned });
      load();
    } catch (err) {
      alert('No se pudo actualizar: ' + (friendlyError(err)));
    }
  };

  const copy = async (m) => {
    // El listado no trae content. Pedimos detalle al hacer click si no está ya cargado.
    let text = m.content;
    if (!text) {
      try {
        const { getMaterial } = await import('../../../services/messagingService');
        const full = await getMaterial(m.id);
        text = full.content;
      } catch {
        alert('No se pudo cargar el contenido para copiar');
        return;
      }
    }
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(m.id);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  const expand = async (m) => {
    if (expandedId === m.id) {
      setExpandedId(null);
      return;
    }
    if (!m.content) {
      try {
        const { getMaterial } = await import('../../../services/messagingService');
        const full = await getMaterial(m.id);
        setItems((arr) => arr.map((it) => (it.id === m.id ? { ...it, content: full.content } : it)));
      } catch (err) {
        alert('No se pudo expandir: ' + err.message);
        return;
      }
    }
    setExpandedId(m.id);
  };

  return (
    <>
      <Toolbar>
        <SearchWrap>
          <FaSearch />
          <TechInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por título o tema…"
            style={{ paddingLeft: '2rem' }}
          />
        </SearchWrap>
        <TechSelect value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">Todos los tipos</option>
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
              {totalsByType[o.value] ? ` · ${totalsByType[o.value]}` : ''}
            </option>
          ))}
        </TechSelect>
        <TechButtonGhost type="button" onClick={load} disabled={loading}>
          <FaSyncAlt /> Refrescar
        </TechButtonGhost>
      </Toolbar>

      <div style={{ marginBottom: '0.85rem', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <StatusPill>Total <strong>{items.length}</strong></StatusPill>
        {Object.entries(totalsByType).map(([t, n]) => (
          <StatusPill key={t}>{typeLabel(t)} <strong>{n}</strong></StatusPill>
        ))}
      </div>

      <TechSection title="Biblioteca de materiales">
        {loading && <Loading>Cargando biblioteca</Loading>}
        {!loading && error && <ErrorBox>No se pudo cargar · {error}</ErrorBox>}
        {!loading && !error && items.length === 0 && (
          <Empty>
            Sin materiales todavía. Generá uno desde el tab <strong>Copilot</strong> y guardalo
            con el botón <em>"Guardar en biblioteca"</em>.
          </Empty>
        )}

        {!loading && !error && items.length > 0 && (
          <Cards>
            {items.map((m) => {
              const isExpanded = expandedId === m.id;
              const tags = Array.isArray(m.tags) ? m.tags : [];
              return (
                <Card key={m.id} $pinned={m.pinned}>
                  <CardTitle>
                    {m.pinned && <FaThumbtack style={{ color: 'var(--alert-warning)', flexShrink: 0, marginTop: 4 }} />}
                    {m.title}
                  </CardTitle>

                  <Meta>
                    <span className="type">{typeLabel(m.type)}</span>
                    {m.channel && <span>{m.channel}</span>}
                    {m.audience && <span>{m.audience}</span>}
                    {m.tone && <span>{m.tone}</span>}
                    <span>{fmtDate(m.created_at)}</span>
                    {m.source === 'ai' && <span style={{ color: accent }}>IA</span>}
                  </Meta>

                  {tags.length > 0 && (
                    <Tags>
                      {tags.slice(0, 6).map((t, i) => (
                        <Tag key={i}><FaHashtag size={9} /> {t}</Tag>
                      ))}
                    </Tags>
                  )}

                  {isExpanded && m.content && (
                    <Preview $expanded>{m.content}</Preview>
                  )}
                  {!isExpanded && m.content && (
                    <Preview>{m.content}</Preview>
                  )}

                  <Actions>
                    <TechButtonGhost type="button" onClick={() => expand(m)}>
                      <FaFileAlt /> {isExpanded ? 'Colapsar' : (m.content ? 'Ver completo' : 'Ver')}
                    </TechButtonGhost>
                    <TechButtonGhost type="button" onClick={() => copy(m)}>
                      {copiedId === m.id ? <><FaCheck /> Copiado</> : <><FaCopy /> Copiar</>}
                    </TechButtonGhost>
                    <TechButtonGhost type="button" onClick={() => togglePin(m)}>
                      <FaThumbtack /> {m.pinned ? 'Desfijar' : 'Fijar'}
                    </TechButtonGhost>
                    <TechButtonDanger type="button" onClick={() => remove(m)}>
                      <FaTrash />
                    </TechButtonDanger>
                  </Actions>
                </Card>
              );
            })}
          </Cards>
        )}
      </TechSection>

      <p style={{
        marginTop: '1rem',
        fontFamily: mono,
        fontSize: '0.62rem',
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: 'var(--text-disabled)',
        textAlign: 'center',
      }}>
        Cada material queda guardado y disponible para todo el equipo
      </p>
    </>
  );
};

export default MaterialsTab;
